import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import get from 'lodash/get';
import isObject from 'lodash/isObject';

import { prepareStormUploadAndCreateBatch } from '@wetransfer/storm-client';

import { ActionHelper } from '../../core-bundle/store/actions-generator';
import { TransferConfig } from '../config/transfer-config';
import { TRANSFER_STATES } from '../constant/transfer-states';
import { TRANSFER_TYPES } from '../constant/transfer-types';
import { getCreateTransferRequestParams } from '../helpers/get-create-transfer-request-params.js';
import {
  canFilesBeAdded,
  hasStarted,
  validateTransfer,
  isTransferWithinRange,
} from '../helpers/index';
import { makeTransferEventData } from '../helpers/make-transfer-event-data';
import uploadMonitoringTracker from '../helpers/uploadMonitoringTracker';
import TransferItem from '../models/transfer-item';
import { TransferAPI, finalizeTransfer } from '../services/api/transfer';
import {
  DD_UPLOAD_CANCELLED,
  DD_UPLOAD_COMPLETED,
  DD_UPLOAD_CREATED,
  DD_UPLOAD_START,
} from '@wetransfer/transfer/constant/datadog';
import { FF_TRANSFER_SIZE_TOOLTIP_EXPERIMENT } from '@wetransfer/transfer/constant/experiments';
import ExperimentWorkspaceTrackingImplementation from '@wetransfer/transfer/constant/experiments/workspace-tracking-implementation';
import FFStormClientDebugMode from '@wetransfer/transfer/constant/flags/ff-storm-client-debug-mode';
import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import Helper from '@wetransfer/transfer/helper';
import TransferError from '@wetransfer/transfer/helper/error/index';
import TransferErrorMessages from '@wetransfer/transfer/helper/error/messages';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { filterUndefinedProperties } from '@wetransfer/transfer/modules/analytics/helpers';
import { implementTransferGroup } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import {
  getExpirationDate,
  getExpirationLabel,
} from '@wetransfer/transfer/modules/analytics/vendors/utils/getUploadExpiry';
import Timer from '@wetransfer/transfer/modules/concorde-timer';
import ErrorHandler from '@wetransfer/transfer/modules/error-handler';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import { HttpResponseCodes } from '@wetransfer/transfer/modules/http/axios/response';
import I18n from '@wetransfer/transfer/modules/i18n';
import Logger from '@wetransfer/transfer/modules/logger';
import Metric from '@wetransfer/transfer/modules/metric';
import Timers from '@wetransfer/transfer/modules/timers';
import {
  notifyUploadSuccess,
  getIsWindowBlurred,
} from '@wetransfer/transfer/modules/upload-notifications/upload-notifications';
import NotificationActions, {
  actions as NotificationActionTypes,
} from '@wetransfer/transfer/store/action/notification';
import TransferActions, {
  actions as TransferActionTypes,
  dispatchNotification,
  getUserType,
} from '@wetransfer/transfer/store/action/transfer';
import { selectTransferAmplitudeEventWallpaperProperties } from '@wetransfer/transfer/store/selector/analytics';
import {
  getCurrentSize,
  getIsForwardedTransfer,
} from '@wetransfer/transfer/transfer-bundle/store/selectors';

import { prepareTransferDownload } from './download-actions';

let resumeStormUpload = null;
let pauseStormUpload = null;
let cancelStormUpload = null;

export const TransferUploadActionTypes = ActionHelper.generateTypes([
  'RESET_UPLOAD_PROGRESS',
  'UPDATE_UPLOAD_TIME',
  'SET_LAST_UPDATE',
  'UPDATE_UPLOAD_PROGRESS',
]);

export const TransferUploadActions = ActionHelper.generateActions({
  resetUploadProgress: [TransferUploadActionTypes.RESET_UPLOAD_PROGRESS],
  updateUploadTime: [
    TransferUploadActionTypes.UPDATE_UPLOAD_TIME,
    'bytesSentSoFar',
    'currentSize',
  ],
  setLastUpdate: [TransferUploadActionTypes.SET_LAST_UPDATE],
  updateUploadProgress: [
    TransferUploadActionTypes.UPDATE_UPLOAD_PROGRESS,
    'bytesSentSoFar',
    'currentSize',
    'transferUploadStartedAt',
  ],
  pauseUpload,
  resumeUpload,
  cancelUpload,
  resetTransfer,
  abortTransfer,
  pauseUploadQueue,
  resumeUploadQueue,
  stopUploadProgress,
  startUploadProgress,
  pauseUploadProgress,
  estimateUploadTime,
  calculateUploadProgress,
  verifyPassword,
  abortUploadOnRefresh,
  createTransfer,
  processUpload,
  startUpload,
});

let remainingTimer;
let progressTimer;

function estimateUploadTime(transferFiles, currentSize) {
  return (dispatch) => {
    dispatch(TransferUploadActions.setLastUpdate());

    if (remainingTimer) {
      remainingTimer.stop();
    }

    remainingTimer = new Timer(
      TransferConfig.upload.remainingTimeInterval,
      () => {
        // We use the transfer files inside the action as the files hold a
        // reference to the bytes sent, and we need to calculate the
        // whole amount of bytes sent every time this action runs.
        const bytesSentSoFar = transferFiles.reduce(
          (count, file) => count + file.bytesSent,
          0
        );

        dispatch(
          TransferUploadActions.updateUploadTime(bytesSentSoFar, currentSize)
        );

        dispatch(
          TransferUploadActions.estimateUploadTime(transferFiles, currentSize)
        );
      }
    );
  };
}

function stopUploadProgress() {
  return (dispatch) => {
    dispatch(TransferUploadActions.pauseUploadProgress());
    dispatch(TransferUploadActions.resetUploadProgress());
  };
}

function calculateUploadProgress(
  transferFiles,
  currentSize,
  transferUploadStartedAt
) {
  return (dispatch) => {
    // We use the transfer files inside the action as the files hold a
    // reference to the bytes sent, and we need to calculate the
    // whole amount of bytes sent every time this action runs.
    const bytesSentSoFar = transferFiles.reduce(
      (count, file) => count + file.bytesSent,
      0
    );

    dispatch(
      TransferUploadActions.updateUploadProgress(
        bytesSentSoFar,
        currentSize,
        transferUploadStartedAt
      )
    );

    uploadMonitoringTracker().collect(
      'upload progress',
      {
        bytesSentSoFar,
        currentSize,
        fileCount: transferFiles?.length,
        percentage: ((bytesSentSoFar / currentSize) * 100).toFixed(2),
        bucketedPercentage: ((bytesSentSoFar / currentSize) * 100).toFixed(),
      },
      'INFO',
      true
    );

    if (progressTimer) {
      progressTimer.stop();
    }

    progressTimer = new Timer(TransferConfig.upload.progressInterval, () => {
      dispatch(
        TransferUploadActions.calculateUploadProgress(
          transferFiles,
          currentSize,
          transferUploadStartedAt
        )
      );
    });
  };
}

function startUploadProgress(
  transferFiles,
  currentSize,
  transferUploadStartedAt
) {
  return (dispatch) => {
    dispatch(
      TransferUploadActions.estimateUploadTime(transferFiles, currentSize)
    );
    dispatch(
      TransferUploadActions.calculateUploadProgress(
        transferFiles,
        currentSize,
        transferUploadStartedAt
      )
    );
  };
}

function pauseUploadProgress() {
  return () => {
    if (remainingTimer) {
      remainingTimer.stop();
      remainingTimer = null;
    }

    if (progressTimer) {
      progressTimer.stop();
      progressTimer = null;
    }
  };
}

function pauseUploadQueue() {
  return pauseStormUpload;
}

function resumeUploadQueue() {
  return resumeStormUpload;
}

function abortUploadQueue() {
  return () => {
    if (cancelStormUpload) {
      cancelStormUpload();
    }
  };
}

export function pauseUpload() {
  return (dispatch) => {
    dispatch(pauseUploadQueue());

    dispatch(TransferUploadActions.pauseUploadProgress());

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.PAUSED,
    });
  };
}

export function resumeUpload(transfer) {
  return (dispatch) => {
    dispatch(resumeUploadQueue());

    dispatch(
      TransferUploadActions.startUploadProgress(
        transfer.files,
        getCurrentSize({ transfer }),
        transfer.uploadStartedAt
      )
    );

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.STARTED,
    });
  };
}

function verifyPassword(
  transferId,
  secret,
  recipient,
  password,
  isOnline = true
) {
  return async (dispatch) => {
    try {
      const fetchedTransfer = await TransferAPI.fetchTransfer(
        transferId,
        secret,
        recipient,
        password,
        false
      );

      dispatch(TransferActions.verifiedPassword(password));
      dispatch(prepareTransferDownload(fetchedTransfer, password));
      dispatch(NotificationActions.clearTransfer());
    } catch (error) {
      dispatch(
        NotificationActions.pushTransfer({
          text: isOnline ? 'InvalidPassword' : 'NoInternet',
        })
      );
    }
  };
}

export function abortUploadOnRefresh(transfer) {
  return async () => {
    if (
      transfer.id &&
      (transfer.state === TRANSFER_STATES.STARTED ||
        transfer.state === TRANSFER_STATES.PAUSED)
    ) {
      await TransferAPI.exitTransfer(transfer.id);
    }
  };
}

export function cancelUpload(transfer, isUserRequest = false) {
  return (dispatch) => {
    datadogRum.addAction(DD_UPLOAD_CANCELLED, {
      isUserRequest,
      transfer_id: transfer.id,
      transfer_type: transfer.type === 'link' ? 'Link' : 'Email',
      domainUserId: transfer.domainUserId,
      userType: transfer.userType,
      files: transfer.files.length,
      transfer_size: transfer.size,
      user_speed: navigator?.connection?.downlink,
    });

    datadogLogs.logger.info('cancelled upload', {
      transfer_id: transfer.id,
      isUserRequest,
    });

    dispatch(abortUploadQueue());

    if (transfer.id) {
      TransferAPI.cancelTransfer(transfer.id);
    }

    transfer.files.forEach((file) => file.reset());

    dispatch(TransferUploadActions.resetUploadProgress());
    dispatch({ type: TransferActionTypes.UPLOAD_RESET });
  };
}

function abortTransfer(transfer) {
  return (dispatch) => {
    dispatch(cancelUpload(transfer));

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.ABORTED,
    });
  };
}

export function resetTransfer() {
  return (dispatch) => {
    dispatch({ type: TransferActionTypes.TRANSFER_RESET });
  };
}

export function createTransfer({
  transfer,
  isForwardedTransfer,
  deliverableId,
}) {
  return async (dispatch) => {
    try {
      // This clears the tracker for every new transfer done in the session.
      uploadMonitoringTracker().init();

      const transferDetails = await TransferAPI.createTransferWithType(
        transfer.type,
        getCreateTransferRequestParams(transfer, deliverableId)
      );

      if (!transferDetails || !transferDetails.id) {
        throw new TransferError('InvalidTransferId');
      }

      uploadMonitoringTracker().setTransferId(transferDetails.id);

      if (isForwardedTransfer) {
        // Rather than normalizing the transfer, we are using the
        // expiry_in_seconds property directly from the API response,
        // hence the different naming (instead of expiryInSeconds)
        dispatch({
          type: TransferActionTypes.TRANSFER_SET_EXPIRY_IN_SECONDS,
          expiryInSeconds: transferDetails.expiry_in_seconds,
        });

        dispatch({
          type: TransferActionTypes.TRANSFER_SET_SHORT_URL,
          shortURL: transferDetails.shortened_url,
        });
      }

      dispatch({
        type: TransferActionTypes.TRANSFER_SET_ID,
        id: transferDetails.id,
      });

      if (transferDetails.display_name) {
        dispatch({
          type: TransferActionTypes.TRANSFER_SET_DISPLAY_NAME,
          displayName: transferDetails.display_name,
        });
      }

      if (transferDetails.storm_upload_token) {
        dispatch({
          type: TransferActionTypes.TRANSFER_SET_STORM_UPLOAD_TOKEN,
          stormUploadToken: transferDetails.storm_upload_token,
        });
      }

      if (transferDetails.signature) {
        dispatch({
          type: TransferActionTypes.TRANSFER_SET_SIGNATURE,
          signature: transferDetails.signature,
        });
      }

      if (transferDetails.message === 'email_verification_required') {
        dispatch({
          type: TransferActionTypes.UPLOAD_NEED_VERIFICATION,
        });
      }

      datadogRum.addAction(DD_UPLOAD_CREATED, {
        files: transfer.files.length,
        storm: true,
        transfer_id: transfer.id,
        transfer_size: transfer.size,
        transfer_type: transfer.type === 'link' ? 'Link' : 'Email',
        domainUserId: transfer.domainUserId,
        userType: transfer.userType,
      });
    } catch (error) {
      dispatch({
        type: TransferActionTypes.TRANSFER_SET_STATE,
        state: TRANSFER_STATES.ABORTED,
      });

      datadogLogs.logger.log(error.toString(), {
        method: 'createTransfer',
        user_journey: 'upload',
        transfer_id: transfer.id,
        stack_trace: error.stack,
        transfer: {
          domainUserId: transfer.domainUserId,
          expiry: transfer.expiry,
          files: transfer.files?.length,
          password: Boolean(transfer.password),
          userType: transfer.userType,
          size: transfer.size,
        },
      });

      ErrorHandler.track(error);

      const isSpecificError = [
        TransferErrorMessages.LIMIT_REACHED,
        TransferErrorMessages.USER_BLOCKED,
        TransferErrorMessages.UNAVAILABLE_PARENT,
        TransferErrorMessages.HUMAN_VERIFICATION_REQUIRED,
        TransferErrorMessages.TOO_MANY_RECIPIENTS,
      ].includes(error.message);

      if (isSpecificError) {
        throw error;
      }

      throw new TransferError('TransferFailed', { transferId: transfer.id });
    }
  };
}

export function uploadTransfer({
  channel,
  account,
  user,
  session,
  allowedSize,
  isForwardedTransfer,
  currentSize,
  deliverableId,
}) {
  return async (dispatch, getState) => {
    dispatch({ type: TransferActionTypes.UPLOAD_PENDING });
    dispatch({
      type: TransferActionTypes.TRANSFER_SET_LANGUAGE,
      language: session.language,
    });
    dispatch({
      type: TransferActionTypes.TRANSFER_SET_DOMAIN_USER_ID,
      domainUserId: session.domainUserId,
    });
    // Set the transfer channel if logged in + has subdomain but not ON a channel
    // Which is almost impossible, besides after just changing your subdomain
    const isChannel = channel && channel.name;
    const hasSubdomain = account && account.subdomain_name;

    if (!isChannel && user.loggedIn && hasSubdomain) {
      dispatch({
        type: TransferActionTypes.TRANSFER_SET_CHANNEL,
        channel: account.subdomain_name,
      });
    }

    try {
      validateTransfer({
        transfer: getState().transfer,
        maximumNumberOfRecipients: user.rules.maximumNumberOfRecipients,
        maximumNumberOfFiles: user.rules.maximumNumberOfFiles,
        allowedSize,
        currentSize,
      });
    } catch (error) {
      dispatchNotification(
        dispatch,
        getState().transfer,
        channel,
        error.message
      );
      throw error;
    }

    try {
      await dispatch(
        TransferUploadActions.createTransfer({
          transfer: getState().transfer,
          isForwardedTransfer,
          deliverableId,
        })
      );

      if (!getState().transfer.verification_required) {
        await dispatch(
          TransferUploadActions.startUpload({
            transfer: getState().transfer,
            channel,
            currentSize,
            isForwardedTransfer,
          })
        );
      }
    } catch (error) {
      const errorMessage = error.message;

      if (errorMessage === TransferErrorMessages.HUMAN_VERIFICATION_REQUIRED) {
        dispatch({
          type: TransferActionTypes.RECAPTCHA_CHALLENGE_RECEIVED,
          transfer_id: error.transferId,
        });
      } else {
        dispatch({
          type: TransferActionTypes.UPLOAD_ERROR,
          error: errorMessage,
        });
        dispatchNotification(
          dispatch,
          getState().transfer,
          channel,
          errorMessage
        );
      }
    }
  };
}

export function createForward({ transferToForward, allowedSize, currentSize }) {
  return async (dispatch, getState) => {
    dispatch(abortUploadQueue());

    dispatch(TransferUploadActions.stopUploadProgress());

    let transferType = transferToForward.type;
    let sender = transferToForward.sender;

    // If it's a link we'll just switch back to e-mail
    // and use last sender from cookie.
    if (transferType === TRANSFER_TYPES.LINK) {
      sender = Helper.getLastUsedSender();
      transferType = TRANSFER_TYPES.EMAIL;
    }

    let state = getState();

    // Sometimes we still haven't filled in a sender
    // even though we DO have one. Let's use that!
    if (state.transfer.sender && !sender) {
      sender = state.transfer.sender;
    }

    const defaultExpiry = get(
      state.user,
      'preferences.transfer_default_expiry_string'
    );

    const expiry = defaultExpiry ? defaultExpiry : transferToForward.expiry;

    await dispatch(
      TransferActions.init({
        userType: getUserType({
          user: state.user,
          channel: state.channel,
          transferFromState: state.transfer,
        }),
        expiry,
        defaultProExpiry: state?.user?.rules?.defaultProExpiry,
        defaultFreeExpiry: state?.user?.rules?.defaultFreeExpiry,
        transferType,
        accountId: state.account.id,
        channelName: transferToForward.channel,
        message: transferToForward.message,
        displayName: transferToForward.display_name,
        parentId: transferToForward.id,
        sender,
        recipients: Helper.isDefaultRecipientInUploadLocked({
          channelDefaultRecipient: state.channel.defaultRecipient,
          userLoggedIn: state.user.loggedIn,
          channelName: state.channel.name,
          accountSubDomainName: state.account.subdomain_name,
        })
          ? state.transfer.recipients
          : [],
      })
    );

    state = getState();

    dispatch(
      addFiles({
        transfer: state.transfer,
        files: transferToForward.files,
        allowedSize,
        currentSize,
      })
    );
  };
}

export function addFiles({
  transfer,
  files,
  allowedSize,
  currentSize,
  maximumNumberOfFiles,
  shouldThrowError = false,
}) {
  return async (dispatch, getState) => {
    try {
      if (hasStarted(transfer.state, transfer.id)) {
        throw new Error('TransferStarted');
      }

      if (!files) {
        throw new Error('MissingFiles');
      }

      // We use `Array.prototype.reduce.call` as `files` array will not always
      // be an instance of `Array`. In the case of IE, it is a `FileList`.
      const validatedFiles = Array.prototype.reduce.call(
        files,
        (result, file) => {
          const transferItem = new TransferItem(file);

          return [...result, transferItem];
        },
        []
      );

      const state = getState();
      const isForwardedTransfer = getIsForwardedTransfer(state);

      const tooltipTextExperiment =
        getVariant(FF_TRANSFER_SIZE_TOOLTIP_EXPERIMENT) === 'on';

      if (!isForwardedTransfer) {
        if (
          !canFilesBeAdded({ currentSize, files: validatedFiles, allowedSize })
        ) {
          throw new Error('TooManyBytes');
        }

        if (tooltipTextExperiment) {
          if (
            state.user.planTier === PLAN_TIER.FREE ||
            (state.user.planTier === undefined && state.channel.name === false)
          ) {
            if (
              isTransferWithinRange({
                currentSize,
                files: validatedFiles,
                allowedSize,
              })
            ) {
              dispatch(
                NotificationActions.pushTransfer({
                  text: 'TransferSizeUpgrade',
                })
              );
            }
          }
        }

        const totalFileCount = validatedFiles.length + transfer.files.length;

        if (totalFileCount > maximumNumberOfFiles) {
          throw new Error('TooManyFiles');
        }
      }

      if (checkForDuplicates(validatedFiles, transfer.itemsTree.items)) {
        throw new Error('DuplicateFilename');
      }

      dispatch({
        type: TransferActionTypes.TRANSFER_ADD_FILES,
        files: validatedFiles,
      });
    } catch (error) {
      ErrorHandler.track(error);

      dispatch({
        type: NotificationActionTypes.NOTIFICATION_TRANSFER_SET,
        text: error.message,
      });

      if (shouldThrowError) {
        throw error;
      }
    }
  };
}

export function validateRecaptcha({
  token,
  transfer,
  channel,
  isForwardedTransfer,
  currentSize,
}) {
  return async (dispatch) => {
    try {
      if (!transfer.id) {
        throw new TransferError(TransferErrorMessages.RECAPTCHA_FAILED);
      }

      await dispatch({
        type: TransferActionTypes.TRANSFER_SET_STATE,
        state: TRANSFER_STATES.INIT,
      });

      await TransferAPI.validateRecaptcha(transfer.id, {
        token,
        expire_after_option: transfer.expiry,
      });

      Metric.send('human_verification_succeeded');
      await dispatch(
        TransferUploadActions.startUpload({
          transfer,
          channel,
          currentSize,
          isForwardedTransfer,
        })
      );
    } catch (error) {
      if (error.message === TransferErrorMessages.RECAPTCHA_FAILED) {
        await dispatch({
          type: TransferActionTypes.TRANSFER_SET_STATE,
          state: TRANSFER_STATES.RECAPTCHA,
        });
      }

      dispatchNotification(dispatch, transfer, channel, error.message);
    }
  };
}

export function verify({ token, isForwardedTransfer, email }) {
  return async (dispatch, getState) => {
    const { transfer, channel } = getState();

    try {
      const transferDetails = await TransferAPI.verifyToken(transfer.id, {
        code: token,
        expire_after_option: transfer.expiry,
      });

      dispatch({ type: TransferActionTypes.UPLOAD_VERIFIED });
      dispatch(
        TransferUploadActions.startUpload({
          transfer,
          channel,
          currentSize: transfer.size,
          isForwardedTransfer,
          stormUploadToken: transferDetails?.storm_upload_token,
          signature: transferDetails?.signature,
        })
      );
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.TOO_MANY_REQUESTS: {
          dispatch(
            NotificationActions.pushTransfer({
              text: 'TransferEmailVerificationThrottleError',
              button: {
                text: I18n.t(
                  'transfer.email-verification.throttle-error-action'
                ),
                action() {
                  dispatch(TransferActions.setState(TRANSFER_STATES.INIT));
                  dispatch(TransferActions.resetVerificationRequired());
                  dispatch({
                    type: TransferActionTypes.TRANSFER_SET_ID,
                    id: false,
                  });
                },
              },
            })
          );
          throw error;
        }

        default: {
          dispatch(
            NotificationActions.pushTransfer({
              text: 'TransferEmailVerificationError',
              button: {
                text: I18n.t(
                  'transfer.email-verification.error-expiry-time-action'
                ),
                action() {
                  dispatch(resendVerifyEmail(email));
                },
              },
            })
          );
          throw error;
        }
      }
    }
  };
}

export function resendVerifyEmail(email) {
  return async (dispatch, getState) => {
    const { transfer } = getState();

    try {
      await TransferAPI.resendToken(transfer.id, {
        sender_email: email,
      });
      dispatch({ type: TransferActionTypes.TRANSFER_SET_SENDER, email });
      dispatch({
        type: TransferActionTypes.TRANSFER_SET_STATE,
        state: TRANSFER_STATES.VERIFY,
      });
    } catch (error) {
      switch (error.status) {
        case HttpResponseCodes.TOO_MANY_REQUESTS:
          dispatch(
            NotificationActions.pushTransfer({
              text: 'TransferEmailVerificationResendThrottleError',
              button: {
                text: I18n.t(
                  'transfer.email-verification-resend.throttle-error-action'
                ),
                action() {
                  dispatch(TransferActions.setState(TRANSFER_STATES.INIT));
                  dispatch(TransferActions.resetVerificationRequired());
                  dispatch({
                    type: TransferActionTypes.TRANSFER_SET_ID,
                    id: false,
                  });
                },
              },
            })
          );
          throw error;
        default:
          dispatch(
            NotificationActions.pushTransfer({
              text: 'TransferEmailVerificationResendError',
            })
          );
          throw error;
      }
    }
  };
}

export function processUpload({
  transfer,
  isForwardedTransfer,
  stormUploadToken,
}) {
  return async (dispatch, getState) => {
    let transferDetails;

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.STARTED,
    });

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_UPLOAD_STARTED_AT,
      uploadStartedAt: Date.now(),
    });

    if (!isForwardedTransfer) {
      try {
        await uploadFiles(transfer, stormUploadToken);
      } catch (error) {
        // storm-client throws when the transfer is canceled by the user.
        if (error.message === 'Canceled via token') {
          return dispatch(TransferUploadActions.cancelUpload(transfer, true));
        }

        dispatch(TransferUploadActions.abortTransfer(transfer));

        Logger.error(error);

        // Error to RUM (0.1% sample rate)
        ErrorHandler.track(error, {
          transfer_id: transfer.id,
          upload_method: 'storm-client',
          short_description: isObject(error?.shortDescription)
            ? JSON.stringify(error?.shortDescription)
            : error?.shortDescription,
          user_journey: 'upload',
        });

        // Error to Datadog logs (100% sample rate)
        datadogLogs.logger.error(error.toString(), {
          method: 'processUpload',
          transfer_id: transfer.id,
          user_journey: 'upload',
          stack_trace: error?.stack,
          upload_method: 'storm-client',
          short_description: isObject(error?.shortDescription)
            ? JSON.stringify(error?.shortDescription)
            : error?.shortDescription,
        });

        throw new TransferError(TransferErrorMessages.TRANSFER_FAILED);
      }

      try {
        if (!transfer.id) {
          throw new TransferError(TransferErrorMessages.MISSING_TRANSFER_ID);
        }

        transferDetails = await finalizeTransfer(transfer.id);
      } catch (error) {
        ErrorHandler.track(error, { transfer_id: transfer.id });

        dispatch({
          type: TransferActionTypes.TRANSFER_SET_STATE,
          state: TRANSFER_STATES.ABORTED,
        });

        throw new TransferError(TransferErrorMessages.TRANSFER_FAILED);
      }

      // Rather than normalizing the transfer, we are using the
      // expiry_in_seconds property directly from the API response,
      // hence the different naming (instead of expiryInSeconds)
      dispatch({
        type: TransferActionTypes.TRANSFER_SET_EXPIRY_IN_SECONDS,
        expiryInSeconds: transferDetails.expiry_in_seconds,
      });

      dispatch({
        type: TransferActionTypes.TRANSFER_SET_SHORT_URL,
        shortURL: transferDetails.shortened_url,
      });

      dispatch({
        type: TransferActionTypes.TRANSFER_SET_SECURITY_HASH,
        securityHash: transferDetails.security_hash,
      });
    }

    dispatch({
      type: TransferActionTypes.TRANSFER_SET_STATE,
      state: TRANSFER_STATES.FINISHED,
    });

    dispatch({
      type: TransferActionTypes.UPLOAD_DONE,
      transfer: {
        ...getState().transfer,
        state: TRANSFER_STATES.PROCESSING,
      },
    });

    return transferDetails;
  };
}

// For Verify and Human-verification the token needs to be send through a param instead of through the store since
// we have that information as a result of calling the /verify | /human-verification endpoint. If we tried to set the store
// with it we receive old information from the store. (Dispatching actions are async)
async function uploadFiles(transfer, stormUploadToken) {
  const stormToken = stormUploadToken || transfer.stormUploadToken;

  if (stormToken) {
    return await uploadFilesUsingStorm({
      ...transfer,
      stormUploadToken: stormToken,
    });
  }
  throw new Error('Upload Token not found');
}

async function uploadFilesUsingStorm(transfer) {
  // The `files` array contains a structure coming from Redux, and the DOM File
  // objects are inside the `ref` property. The Storm client wants native DOM file objects there.
  const actualFiles = transfer.files.filter((item) => item.isFile);

  const domFiles = actualFiles.map((f) => {
    f.ref.overriddenRelativePath = f.safename; // which gets munged on, say, iOS

    return f.ref;
  });

  // The calculateUploadProgress function wants to dig into the actual files of the transfer
  // and sum their bytesSent properties. Since these properties get updated by the chunk uploader,
  // we need to update them as well.
  const onProgress = ({ perFile }) => {
    for (const [fileIdx, { uploaded }] of perFile.entries()) {
      actualFiles[fileIdx].state.bytesSent = uploaded;
    }
  };

  const { pause, resume, cancel, start } = prepareStormUploadAndCreateBatch({
    items: domFiles,
    token: transfer.stormUploadToken,
    onProgress,
    onDebug: (log) => {
      if (log.type === 'ERROR' || FFStormClientDebugMode.isEnabled()) {
        datadogLogs.logger.log(
          `${log.type === 'ERROR' ? '[ERROR] ' : ''}${log.message}`,
          { ...log.details, transfer_id: transfer.id }
        );
      }
    },
  });

  pauseStormUpload = pause;
  resumeStormUpload = resume;
  cancelStormUpload = cancel;

  return start();
}

export function startUpload({
  transfer,
  channel,
  isForwardedTransfer,
  currentSize,
  stormUploadToken,
  signature,
}) {
  return async (dispatch, getState) => {
    const transferEventData = makeTransferEventData({
      transfer,
      mode: 'upload',
      isForwardedTransfer,
      currentSize,
    });

    Analytics.trackGoogleEvent({
      category: 'Transfer',
      action: 'Upload',
      label: transfer.type,
      value: currentSize,
      transfer: transferEventData,
      user: {
        download_count: Helper.getWTDownloaded(),
        upload_count: Helper.getWTSent(),
        first_visit_date: Helper.getFirstVisitDate(),
      },
    });

    const state = getState();

    const {
      wallpaperType,
      wallpaperFileType,
      wallpaperNumber,
      wallpaperSource,
    } = selectTransferAmplitudeEventWallpaperProperties(state);

    const isRecipientLocked = Helper.isDefaultRecipientInUploadLocked({
      channelDefaultRecipient: channel.defaultRecipient,
      userLoggedIn: state.user.loggedIn,
      channelName: channel.name,
      accountSubDomainName: state.account.subdomain_name,
    });
    // If there is a default recipient locked, it means user is uploading the transfer from someone else's subdomain.
    const userUploadRole = isRecipientLocked ? 'Uploader' : 'Sender';

    const amplitudeTransferEvent = {
      'Transfer Type': transfer.type === 'link' ? 'Link' : 'Email',
      'File Size': Helper.getTransferSizeInMB(transfer.size),
      'Password Protection': transfer.password ? 'Yes' : 'No',
      'Number of Files': transfer.files.length,
      'Expiration Date': getExpirationDate(transfer.expiry),
      'Expiration Duration': getExpirationLabel(transfer.expiry),
      'Created By': userUploadRole,
      'Number of Recipients':
        transfer.type === 'link' ? null : transfer.recipients.length,
      ...filterUndefinedProperties({
        'Wallpaper Type': wallpaperType,
        'Wallpaper File': wallpaperFileType,
        'Wallpaper Number': wallpaperNumber,
        'Wallpaper Source': wallpaperSource,
      }),
    };

    if (ExperimentWorkspaceTrackingImplementation.isWorkspaceTracking()) {
      amplitudeTransferEvent['File Size in Bytes'] = transfer.size;
    }

    implementTransferGroup({
      transferId: transfer.id,
      fileSizeInBytes: transfer.size,
      userUploadRole,
    });

    Analytics.trackAmplitude({
      action: 'Transfer Sent',
      groups: {
        'Transfer ID': transfer.id,
      },
      additionalProperties: amplitudeTransferEvent,
    });

    datadogRum.addAction(DD_UPLOAD_START, {
      files: transfer.files.length,
      storm: true,
      transfer_id: transfer.id,
      transfer_size: transfer.size,
      transfer_type: transfer.type === 'link' ? 'Link' : 'Email',
      user_speed: navigator?.connection?.downlink,
      domainUserId: transfer.domainUserId,
      userType: transfer.userType,
    });

    Timers.start('transfer.upload');

    // Start the Transfer, monitor changes and also
    // catch errors if they occur.
    try {
      dispatch(
        TransferUploadActions.startUploadProgress(
          transfer.files,
          currentSize,
          transfer.uploadStartedAt
        )
      );

      dispatch({ type: TransferActionTypes.UPLOAD_ERROR_RESET });

      if (signature) {
        dispatch({
          type: TransferActionTypes.TRANSFER_SET_SIGNATURE,
          signature,
        });
      }

      const { uploadNotifications } = getState();

      // Upload actual transfer
      const uploadedTransfer = await dispatch(
        TransferUploadActions.processUpload({
          transfer,
          isForwardedTransfer,
          stormUploadToken,
        })
      );

      Helper.incrementWTSent();

      transferEventData.success = true;
      transferEventData.cancelled = false;
      transferEventData.duration = Timers.stop('transfer.upload');

      const isInBackgroundAndActive =
        uploadNotifications.active && getIsWindowBlurred();

      Analytics.trackAmplitude({
        action: 'Transfer Completed',
        groups: {
          'Transfer ID': transfer.id,
        },
        additionalProperties: {
          ...amplitudeTransferEvent,
          ...(isInBackgroundAndActive && { Notification: 'Enabled' }),
        },
      });

      datadogRum.addAction(DD_UPLOAD_COMPLETED, {
        files: transfer.files.length,
        transfer_size: transfer.size,
        upload_speed: transferEventData.duration
          ? ((transfer.size / transferEventData.duration) * 1000) / 1024 / 1024
          : -1,
        user_speed: navigator?.connection?.downlink,
        duration: transferEventData.duration,
        storm: true,
        transfer_id: transfer.id,
        transfer_type: transfer.type === 'link' ? 'Link' : 'Email',
        domainUserId: transfer.domainUserId,
        userType: transfer.userType,
      });

      if (uploadedTransfer) {
        // We only want to cover the count of directories on highest level (not including possible nested)
        const directories = transfer.itemsTree.items.filter(
          (item) => item.uiType === 'directory'
        );

        // This map is needed because we have 2 sources of data:
        // `transfer` (formed from input data) and `uploadedTransfer` (contains additional data from backend)
        // `transfer` has extension and size, but missing backend IDs.
        // `uploadedTransfer` is missing extension, but has IDs and size.
        const itemsExtensionMap = transfer.files.reduce((map, item) => {
          map[item.name] = item.type;

          return map;
        }, {});

        Analytics.trackUnstructSnowplowEvent(
          'iglu:com.wetransfer/transfer_event/jsonschema/2-0-0',
          {
            transfer_id: uploadedTransfer.id,
            transfer_type:
              uploadedTransfer.recipients.length > 0 ? 'EMAIL' : 'LINK',
            expiration_date: uploadedTransfer.expires_at,
            expiration_duration: transfer.expiry,
            password_protected: uploadedTransfer.password_protected,
            email_sender_recipient: uploadedTransfer.recipients.some(
              (recipient) => recipient.email === uploadedTransfer.creator.email
            ),
            email_number_recipients: uploadedTransfer.recipients.length,
            number_files: uploadedTransfer.files.length,
            number_folders: directories.length,
            files_total_size: uploadedTransfer.files.reduce(
              (sum, file) => sum + file.size,
              0
            ),
            files: uploadedTransfer.files.map((item) => ({
              id: item.id,
              extension: itemsExtensionMap[item.name],
              size: item.size,
            })),
          }
        );

        // Notify the user that the transfer is done
        if (uploadNotifications.active) {
          notifyUploadSuccess(uploadNotifications.sound);
        }
      }
    } catch (error) {
      uploadMonitoringTracker().flush();
      datadogLogs.logger.error(error.toString(), {
        method: 'startUpload',
        user_journey: 'upload',
        transfer_id: transfer.id,
        stack_trace: error.stack,
        upload_method: 'storm-client',
      });

      dispatch({ type: TransferActionTypes.UPLOAD_ERROR, error });
      dispatchNotification(dispatch, transfer, channel, error);
    } finally {
      dispatch(TransferUploadActions.stopUploadProgress());

      dispatch({ type: TransferActionTypes.UPLOAD_RESET });
    }
  };
}

function checkForDuplicates(newItems, allItems) {
  const files = [];
  newItems.forEach((file) => {
    files.push(file.safename);
  });
  // create safename for existing file tree entries
  allItems.forEach((file) => {
    files.push(file.name.trim());
  });

  const uniqueFilenames = [...new Set(files)];

  return uniqueFilenames.length !== files.length;
}
